import axios, { AxiosRequestConfig } from "axios";

export interface ContractStatusResponse {
  contractType: "employee" | "manager" | null;
  contractVersion: number;
  requiredContracts: string[];
  contracts: {
    contractKey: string;
    title?: string;
    accepted: boolean;
    contractVersion: number;
  }[];
  company?: {
    id?: number;
    name?: string;
  };
}

export interface ContractContentResponse {
  contractKey: string;
  content: string;
}

export interface ContractAcceptResponse {
  accepted: boolean;
}

class ContractsService {
  private static instance: ContractsService;

  public static getInstance(): ContractsService {
    if (!ContractsService.instance) ContractsService.instance = new ContractsService();
    return ContractsService.instance;
  }

  private constructor() {}

  private getAuthHeaders(): AxiosRequestConfig {
    const token = localStorage.getItem("token");
    const bearerToken = token
      ? token.startsWith("Bearer ")
        ? token
        : `Bearer ${token}`
      : "";
    return {
      headers: bearerToken ? { Authorization: bearerToken } : {},
    };
  }

  private getBaseCandidates(): string[] {
    const configured = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    const fromConfiguredRoot = configured.replace(/\/api$/i, "");

    const candidates = [
      configured, // ör: https://dinox.app/api
      fromConfiguredRoot, // ör: https://dinox.app
    ].filter(Boolean);

    return Array.from(new Set(candidates));
  }

  private async tryGet<T>(paths: string[]): Promise<T> {
    const bases = this.getBaseCandidates();
    let lastError: any = null;

    for (const base of bases) {
      for (const path of paths) {
        const url = `${base}${path}`;
        try {
          const response = await axios.get(url, this.getAuthHeaders());
          return (response.data?.data || response.data) as T;
        } catch (error: any) {
          lastError = error;
        }
      }
    }

    throw lastError;
  }

  private async tryPost<T>(paths: string[], data: any): Promise<T> {
    const bases = this.getBaseCandidates();
    let lastError: any = null;

    for (const base of bases) {
      for (const path of paths) {
        const url = `${base}${path}`;
        try {
          const response = await axios.post(url, data, this.getAuthHeaders());
          return (response.data?.data || response.data) as T;
        } catch (error: any) {
          lastError = error;
        }
      }
    }

    throw lastError;
  }

  public async getStatus(): Promise<ContractStatusResponse> {
    return this.tryGet<ContractStatusResponse>([
      "/contracts/status",
      "/api/contracts/status",
    ]);
  }

  public async getContent(contractKey: string): Promise<ContractContentResponse> {
    const encoded = encodeURIComponent(contractKey);
    return this.tryGet<ContractContentResponse>([
      `/contracts/content?contractKey=${encoded}`,
      `/api/contracts/content?contractKey=${encoded}`,
    ]);
  }

  public async accept(contractKey: string): Promise<ContractAcceptResponse> {
    return this.tryPost<ContractAcceptResponse>(
      ["/contracts/accept", "/api/contracts/accept"],
      { contractKey }
    );
  }
}

export default ContractsService.getInstance();

